import { ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import globalShared from '@/shared';
import {
  confirmSweepstakesWinners,
  drawSweepstakesWinners,
} from '@/graphs/gql';
import { parseSweepstakesActionError } from '@/utils/sweepstakesLifecycle';

export function useSweepstakesActions(onSuccess?: () => void | Promise<void>) {
  const { makeGraphRequest, getAuthToken } = globalShared();
  const actionLoading = ref(false);

  async function runAction(action: () => Promise<void>) {
    actionLoading.value = true;
    try {
      await action();
      if (onSuccess) {
        await onSuccess();
      }
    } catch (error) {
      console.error('Sweepstakes action failed:', error);
      ElMessage.error(parseSweepstakesActionError(error));
    } finally {
      actionLoading.value = false;
    }
  }

  async function replaceWinner(stickerId: string, winnerRowId: string) {
    await runAction(async () => {
      const response = await makeGraphRequest(
        'mutation',
        drawSweepstakesWinners,
        {
          sweepstakesStickerId: stickerId,
          participantIds: [winnerRowId],
        },
        getAuthToken(),
      );

      if (response?.errors?.length) {
        throw new Error(response.errors[0]?.message);
      }

      ElMessage.success('Winner replaced. Only the new winner was notified.');
    });
  }

  async function confirmSweepstakes(stickerId: string) {
    try {
      await ElMessageBox.confirm(
        'Close this sweepstakes permanently? Winners can no longer be replaced and no emails will be sent.',
        'Close sweepstakes',
        {
          type: 'warning',
          confirmButtonText: 'Close sweepstakes',
          cancelButtonText: 'Cancel',
        },
      );
    } catch {
      return;
    }

    await runAction(async () => {
      const response = await makeGraphRequest(
        'mutation',
        confirmSweepstakesWinners,
        { sweepstakesStickerId: stickerId },
        getAuthToken(),
      );

      if (response?.errors?.length) {
        throw new Error(response.errors[0]?.message);
      }

      ElMessage.success('Sweepstakes closed.');
    });
  }

  async function drawEarly(stickerId: string) {
    try {
      await ElMessageBox.confirm(
        'Draw winners now? This will email all winners and non-winners immediately and close entries.',
        'Draw early',
        {
          type: 'warning',
          confirmButtonText: 'Draw now',
          cancelButtonText: 'Cancel',
        },
      );
    } catch {
      return;
    }

    await runAction(async () => {
      const response = await makeGraphRequest(
        'mutation',
        drawSweepstakesWinners,
        {
          sweepstakesStickerId: stickerId,
          participantIds: [],
        },
        getAuthToken(),
      );

      if (response?.errors?.length) {
        throw new Error(response.errors[0]?.message);
      }

      ElMessage.success('Winners drawn and entrants notified.');
    });
  }

  async function handleReplaceWinner(stickerId: string, winnerRowId: string, winnerName: string) {
    try {
      await ElMessageBox.confirm(
        `Replace ${winnerName}? A new winner will be drawn and only they will be emailed.`,
        'Replace winner',
        {
          type: 'warning',
          confirmButtonText: 'Replace',
          cancelButtonText: 'Cancel',
        },
      );
    } catch {
      return;
    }

    await replaceWinner(stickerId, winnerRowId);
  }

  return {
    actionLoading,
    replaceWinner,
    confirmSweepstakes,
    drawEarly,
    handleReplaceWinner,
  };
}
